// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * PubMed API Service
 * 
 * Purpose: API client for PubMed search and details endpoints
 * Endpoints: POST /api/pubmed/search, POST /api/pubmed/details
 * 
 * @note Follows existing API patterns from src/services/apiService.ts and decisionTreeService.ts
 * @note CSRF token handling matches existing implementation
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Date range filter for search queries
 */
export interface DateRange {
  from: string; // ISO date string (YYYY-MM-DD)
  to: string;   // ISO date string (YYYY-MM-DD)
}

/**
 * Sort order for search results
 */
export type SortOrder = "relevance" | "date" | "pubDate";

/**
 * Request format for PubMed search endpoint
 * @see backend/server/pubmed_handlers.R for server-side validation
 */
export interface PubmedSearchRequest {
  query: string;                    // Required: Search query string
  page?: number;                   // Optional, default 0: Page number (0-indexed)
  pageSize?: number;               // Optional, default 20: Results per page (max 100)
  sort?: SortOrder;                // Optional, default "relevance": Sort order
  dateRange?: DateRange;           // Optional: Filter by publication date
}

/**
 * Request format for PubMed details endpoint
 */
export interface PubmedDetailsRequest {
  pmid: string;                   // Required: PubMed ID (PMID) as string
}

/**
 * Paper summary from search results
 */
export interface PubmedPaperSummary {
  pmid: string;
  title: string;
  journal?: string;
  year?: number;
  firstAuthor?: string;
  pubDate?: string;
  doi?: string;
  url: string;
  abstract?: string;
}

/**
 * Full paper details with abstract and metadata
 */
export interface PubmedPaperDetails {
  pmid: string;
  title: string;
  abstract?: string;
  journal?: string;
  year?: number;
  pubDate?: string;
  authors?: string[];
  doi?: string;
  publicationTypes?: string[];
  keywords?: string[];
  meshTerms?: string[];
  url: string;
}

/**
 * Search response data payload
 */
export interface PubmedSearchData {
  query: string;
  page: number;
  pageSize: number;
  total: number;
  items: PubmedPaperSummary[];
}

/**
 * Response format from PubMed search endpoint
 */
export interface PubmedSearchResponse {
  requestId: string;
  data: PubmedSearchData;
}

/**
 * Response format from PubMed details endpoint
 */
export interface PubmedDetailsResponse {
  requestId: string;
  data: PubmedPaperDetails;
}

/**
 * Error object within error response
 * @see backend/server/pubmed_handlers.R for error codes
 */
export interface PubmedApiError {
  code: string;                   // EHTA-PUBMED-XXX format
  message: string;
  details?: Record<string, unknown>;
  retryAfterSeconds?: number;      // Present for rate limit errors (EHTA-PUBMED-010)
}

/**
 * Full error response from PubMed API
 * @see backend/server/pubmed_handlers.R for error codes
 */
export interface PubmedErrorResponse {
  requestId: string;
  error: PubmedApiError;
}

/**
 * API error with structured details
 */
export class PubmedApiErrorClass extends Error {
  public readonly code: string;
  public readonly requestId: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;
  public readonly retryAfterSeconds?: number;

  constructor(errorResponse: PubmedErrorResponse, status: number) {
    super(errorResponse.error.message);
    this.name = 'PubmedApiError';
    this.code = errorResponse.error.code;
    this.requestId = errorResponse.requestId;
    this.status = status;
    this.details = errorResponse.error.details;
    this.retryAfterSeconds = errorResponse.error.retryAfterSeconds;
  }
}

// ============================================================================
// CSRF Token Management (matches existing pattern)
// ============================================================================

let csrfToken: string | null = null;

/**
 * Ensure CSRF token is available for authenticated requests
 * @note Pattern copied from src/services/apiService.ts and decisionTreeService.ts
 * @param forceRefresh - If true, bypass cache and fetch a fresh token
 */
async function ensureCsrf(forceRefresh = false): Promise<string> {
  if (csrfToken && !forceRefresh) return csrfToken;

  try {
    const res = await fetch('/api/auth/csrf', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(5000),
    });

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await res.text();
      const preview = text.slice(0, 120);
      console.warn('CSRF endpoint returned non-JSON. Content-Type:', contentType || 'none', 'First 120 chars:', preview);
      throw new Error(`CSRF endpoint misrouted (content-type: ${contentType || 'none'})`);
    }

    if (!res.ok) {
      console.warn(`CSRF endpoint not available (${res.status}), proceeding without CSRF protection`);
      return 'no-csrf';
    }

    const data = await res.json();
    const { csrf_token } = data;
    csrfToken = csrf_token;
    return csrfToken!;
  } catch (error) {
    if (error instanceof Error && error.message.includes('misrouted')) {
      console.error('CSRF endpoint misrouted - likely NGINX/CloudFront routing issue:', error.message);
    } else {
      console.warn('CSRF endpoint not available, proceeding without CSRF protection:', error);
    }
    return 'no-csrf';
  }
}

/**
 * Invalidate the cached CSRF token (called on CSRF validation errors)
 */
function invalidateCsrfToken(): void {
  csrfToken = null;
}

// ============================================================================
// Generic Fetch Helper
// ============================================================================

/**
 * Handle common error cases for PubMed API calls
 * Transforms errors into user-friendly messages while preserving abort signals
 * 
 * @param error - The caught error
 * @param timeoutMessage - Custom message for timeout errors
 * @throws The transformed error or re-throws the original
 */
function handlePubmedError(error: unknown, timeoutMessage: string): never {
  // Re-throw PubmedApiErrorClass as-is
  if (error instanceof PubmedApiErrorClass) {
    throw error;
  }

  // Handle abort - throw DOMException so component can detect it
  if (error instanceof DOMException && error.name === 'AbortError') {
    throw error; // Component will check for this and ignore silently
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    throw new Error('Network error: Unable to reach PubMed API');
  }

  // Handle timeout
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    throw new Error(timeoutMessage);
  }

  // Re-throw other errors
  throw error;
}

/**
 * Generic fetch helper for PubMed API endpoints
 * Handles CSRF, headers, AbortController composition, and error parsing
 * 
 * @param endpoint - API endpoint path (e.g., '/api/pubmed/search')
 * @param body - Request body to JSON.stringify
 * @param signal - Optional AbortSignal from caller
 * @param timeoutMs - Timeout in milliseconds (default: 30000)
 * @returns Response object
 * @throws PubmedApiErrorClass for structured errors
 * @throws Error for other failures
 */
async function fetchPubmedApi(
  endpoint: string,
  body: unknown,
  signal?: AbortSignal,
  timeoutMs: number = 30000,
  isRetry: boolean = false
): Promise<Response> {
  // Get CSRF token (force refresh on retry)
  const csrf = await ensureCsrf(isRetry);

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Request-Id': crypto.randomUUID?.() ?? String(Date.now()),
  };

  // Only include CSRF token if we have a real one
  if (csrf && csrf !== 'no-csrf') {
    headers['X-CSRF-Token'] = csrf;
  }

  // AbortController composition: Create one controller per request
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Compose caller's signal + timeout signal
  if (signal) {
    signal.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      controller.abort();
    });
  }

  try {
    // Make API request
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    // Cleanup timeout
    clearTimeout(timeoutId);

    // Check Content-Type
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      // Try to parse error response
      let errorData: PubmedErrorResponse | null = null;

      if (contentType.includes('application/json')) {
        try {
          const errorResponse = await response.json();

          // Handle structured error from our R service
          if (errorResponse.error && typeof errorResponse.error === 'object') {
            errorData = errorResponse;
          }
        } catch {
          // Failed to parse JSON error
        }
      }

      if (errorData) {
        // Check for CSRF validation error - retry once with fresh token
        if (errorData.error.code === 'EHTA-CSRF-001' && !isRetry) {
          console.log('CSRF token expired, refreshing and retrying...');
          invalidateCsrfToken();
          return fetchPubmedApi(endpoint, body, signal, timeoutMs, true);
        }

        throw new PubmedApiErrorClass(errorData, response.status);
      }

      // Fallback error
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Validate Content-Type for success responses
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      const preview = text.slice(0, 120);
      console.warn(`${endpoint} returned non-JSON. Content-Type:`, contentType || 'none', 'First 120 chars:', preview);
      throw new Error(`Unexpected response format (content-type: ${contentType || 'none'})`);
    }

    return response;
  } catch (error) {
    // Cleanup timeout on error
    clearTimeout(timeoutId);

    // Re-throw PubmedApiErrorClass as-is
    if (error instanceof PubmedApiErrorClass) {
      throw error;
    }

    // Re-throw other errors to be handled by caller
    throw error;
  }
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Search PubMed for papers matching query
 * 
 * @param request - Search parameters (query, pagination, sort, dateRange)
 * @param signal - AbortSignal for request cancellation
 * @returns Search results with paper summaries
 * @throws PubmedApiErrorClass with structured error details
 * 
 * @example
 * ```typescript
 * const results = await pubmedSearch({
 *   query: "vaccine efficacy influenza",
 *   page: 0,
 *   pageSize: 20
 * });
 * console.log('Found', results.data.total, 'papers');
 * ```
 */
export async function pubmedSearch(
  request: PubmedSearchRequest,
  signal?: AbortSignal
): Promise<PubmedSearchResponse> {
  try {
    const response = await fetchPubmedApi('/api/pubmed/search', request, signal);
    const data: PubmedSearchResponse = await response.json();

    // Response validation: Validate response shape
    if (!data.data || !Array.isArray(data.data.items)) {
      throw new Error('Invalid response format: missing data.items array');
    }

    // Normalize missing fields in items
    const normalizedItems = data.data.items.map((item) => ({
      pmid: item.pmid || '',
      title: item.title || '',
      journal: item.journal || undefined,
      year: typeof item.year === 'number' && item.year > 0 ? item.year : undefined,
      firstAuthor: item.firstAuthor || undefined,
      pubDate: item.pubDate || undefined,
      doi: item.doi || undefined,
      url: item.url || '',
    }));

    return { ...data, data: { ...data.data, items: normalizedItems } };

  } catch (error) {
    handlePubmedError(error, 'Request timeout: PubMed search took too long');
  }
}

/**
 * Get full details for a specific paper by PMID
 * 
 * @param request - Details request with PMID
 * @param signal - AbortSignal for request cancellation
 * @returns Full paper details including abstract
 * @throws PubmedApiErrorClass with structured error details
 * 
 * @example
 * ```typescript
 * const details = await pubmedDetails({ pmid: "12345678" });
 * console.log('Title:', details.data.title);
 * console.log('Abstract:', details.data.abstract);
 * ```
 */
export async function pubmedDetails(
  request: PubmedDetailsRequest,
  signal?: AbortSignal
): Promise<PubmedDetailsResponse> {
  try {
    const response = await fetchPubmedApi('/api/pubmed/details', request, signal);
    const data: PubmedDetailsResponse = await response.json();

    // Response validation: Validate required fields
    if (!data.data || !data.data.pmid || !data.data.title) {
      throw new Error('Invalid response format: missing required fields (pmid or title)');
    }

    // Normalize missing optional fields
    const authors = Array.isArray(data.data.authors)
      ? data.data.authors.filter((a: unknown): a is string => typeof a === 'string' && a.trim().length > 0)
      : undefined;

    const normalized: PubmedPaperDetails = {
      pmid: data.data.pmid || '',
      title: data.data.title || '',
      abstract: data.data.abstract || undefined,
      journal: data.data.journal || undefined,
      year: typeof data.data.year === 'number' && data.data.year > 0 ? data.data.year : undefined,
      pubDate: data.data.pubDate || undefined,
      authors: authors && authors.length > 0 ? authors : undefined,
      doi: data.data.doi || undefined,
      publicationTypes: Array.isArray(data.data.publicationTypes) ? data.data.publicationTypes : undefined,
      keywords: Array.isArray(data.data.keywords) ? data.data.keywords : undefined,
      url: data.data.url || '',
    };

    return { ...data, data: normalized };

  } catch (error) {
    handlePubmedError(error, 'Request timeout: PubMed details request took too long');
  }
}

// ============================================================================
// Error Codes Reference
// ============================================================================
//
// EHTA-PUBMED-001 to EHTA-PUBMED-005: Validation errors
// - EHTA-PUBMED-001: Invalid JSON/missing body
// - EHTA-PUBMED-002: Invalid query
// - EHTA-PUBMED-003: Invalid pagination
// - EHTA-PUBMED-004: Invalid sort
// - EHTA-PUBMED-005: Invalid dateRange
//
// EHTA-PUBMED-010 to EHTA-PUBMED-013: Upstream/Network errors
// - EHTA-PUBMED-010: Upstream rate limit (includes retryAfterSeconds)
// - EHTA-PUBMED-011: Upstream error (5xx)
// - EHTA-PUBMED-012: Upstream timeout
// - EHTA-PUBMED-013: Network/DNS error
//
// EHTA-PUBMED-020 to EHTA-PUBMED-021: Parse/not found errors
// - EHTA-PUBMED-020: Parse error
// - EHTA-PUBMED-021: PMID invalid/not found
//
// ============================================================================

